import React from 'react'
import { useNavigate } from 'react-router-dom'
import AppActionMenu from '../../../components/AppActionMenu'

export default function RemarksTable({
  records = [],
  loading = false,
  sortBy = 'remarkDate',
  sortOrder = 'desc',
  onSort,
  onDelete,
  onSendSms,
  onSendWhatsApp,
}) {
  const navigate = useNavigate()

  const getCategoryBadge = (name = '') => {
    const lower = name.toLowerCase()
    if (lower.includes('appreciation') || lower.includes('outstanding') || lower.includes('praise')) {
      return 'bg-emerald-500/10 text-emerald-800 border-emerald-500/30'
    }
    if (lower.includes('late') || lower.includes('incomplete') || lower.includes('warning')) {
      return 'bg-rose-500/10 text-rose-800 border-rose-500/30'
    }
    if (lower.includes('parent') || lower.includes('meeting') || lower.includes('improvement')) {
      return 'bg-amber-500/10 text-amber-800 border-amber-500/30'
    }
    return 'bg-primary/10 text-primary border-primary/30'
  }

  const formatDate = (dStr) => {
    if (!dStr) return '—'
    try {
      return new Date(dStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dStr
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1000px] text-left text-xs text-on-surface">
          {/* ── Table Header ── */}
          <thead className="border-b border-outline-variant/25 bg-surface-container-low/80 text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant select-none">
            <tr>

              {/* Student / Date */}
              <th
                onClick={() => onSort('fullName')}
                className="w-56 px-4 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>Student / Date</span>
                  {sortBy === 'fullName' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Roll No & Remark Type */}
              <th
                onClick={() => onSort('rollNo')}
                className="w-48 px-4 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>Roll No &amp; Remark</span>
                  {sortBy === 'rollNo' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Course & Batch */}
              <th
                onClick={() => onSort('courseName')}
                className="w-48 px-4 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>Course / Batch</span>
                  {sortBy === 'courseName' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Company & Branch */}
              <th
                onClick={() => onSort('branchName')}
                className="w-44 px-4 py-3.5 cursor-pointer hover:text-primary transition"
              >
                <div className="flex items-center gap-1">
                  <span>Branch / Company</span>
                  {sortBy === 'branchName' && (
                    <span className="material-symbols-outlined text-xs">
                      {sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                    </span>
                  )}
                </div>
              </th>

              {/* Particulars & Faculty */}
              <th className="px-4 py-3.5">
                <span>Particulars / Notes</span>
              </th>

              <th className="w-16 px-4 py-3.5 text-right">Action</th>
            </tr>
          </thead>

          {/* ── Table Body ── */}
          <tbody className="divide-y divide-outline-variant/15 font-medium">
            {loading ? (
              <tr>
                <td colSpan={6} className="py-16 text-center text-xs text-on-surface-variant">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined animate-spin text-2xl text-primary">
                      progress_activity
                    </span>
                    <span>Loading student remarks...</span>
                  </div>
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-16 text-center text-xs text-on-surface-variant">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-4xl text-outline-variant">
                      rate_review
                    </span>
                    <p className="font-bold text-on-surface">No Remarks Found</p>
                    <p className="text-[11px]">No student observation remarks matching current filters.</p>
                  </div>
                </td>
              </tr>
            ) : (
              records.map((record) => (
                <tr
                  key={record.id}
                  className="group transition-colors hover:bg-surface-container-low/50"
                >


                  {/* Student / Date */}
                  <td className="px-4 py-3.5 align-top">
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/remarks/edit/${record.id}`)}
                        className="font-display text-sm font-extrabold text-[#3b82f6] hover:underline text-left cursor-pointer"
                      >
                        {record.fullName}
                      </button>
                      <span className="text-[11px] font-semibold text-on-surface-variant flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs">calendar_today</span>
                        <span>{formatDate(record.remarkDate)}</span>
                      </span>
                    </div>
                  </td>

                  {/* Roll No & Remark Badge */}
                  <td className="px-4 py-3.5 align-top">
                    <div className="flex flex-col gap-1 items-start">
                      <span className="inline-flex items-center gap-1 rounded-lg bg-surface-container-high px-2 py-0.5 text-xs font-black text-on-surface">
                        Roll: {record.rollNo}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-xl border px-2.5 py-0.5 text-[11px] font-bold ${getCategoryBadge(
                          record.remarkName
                        )}`}
                      >
                        {record.remarkName}
                      </span>
                    </div>
                  </td>

                  {/* Course & Batch */}
                  <td className="px-4 py-3.5 align-top">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-bold text-on-surface text-xs">{record.courseName}</span>
                      <span className="text-[11px] text-on-surface-variant">{record.batchNames}</span>
                    </div>
                  </td>

                  {/* Company & Branch */}
                  <td className="px-4 py-3.5 align-top">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-bold text-on-surface text-xs">{record.branchName}</span>
                      <span className="text-[10px] text-on-surface-variant">{record.instituteName}</span>
                    </div>
                  </td>

                  {/* Particulars & Faculty */}
                  <td className="px-4 py-3.5 align-top">
                    <div className="flex flex-col gap-1">
                      <p className="text-xs text-on-surface leading-relaxed line-clamp-2">
                        {record.particulars || 'No additional remarks notes recorded.'}
                      </p>
                      <div className="flex items-center gap-1 text-[11px] font-semibold text-primary">
                        <span className="material-symbols-outlined text-xs">person</span>
                        <span>{record.remarkedBy || 'Faculty'}</span>
                      </div>
                    </div>
                  </td>

                  {/* Actions (Three-Dot AppActionMenu) */}
                  <td className="px-4 py-3.5 align-top text-right">
                    <AppActionMenu
                      triggerLabel=""
                      triggerIcon="more_vert"
                      showChevron={false}
                      triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                      items={[
                        { key: 'edit', label: 'Edit', icon: 'edit' },
                        { key: 'delete', label: 'Delete', icon: 'delete', destructive: true },
                        { key: 'sms', label: 'Send SMS', icon: 'sms' },
                      ]}
                      onSelect={(actionKey) => {
                        if (actionKey === 'edit') navigate(`/remarks/edit/${record.id}`)
                        if (actionKey === 'delete') onDelete?.(record.id)
                        if (actionKey === 'sms') onSendSms?.(record)
                      }}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
